import React, { useState, useEffect, useCallback, useRef } from "react";
import {
	Box,
	Grid,
	Typography,
	Divider,
	Tooltip,
	Badge,
	useMediaQuery,
} from "@mui/material";
import {
	BatteryChargingFull,
	SignalCellularOff,
	SignalCellular4Bar,
	HourglassFull,
	Moving,
	EvStation,
	Loop,
	Bolt,
	ElectricRickshaw,
	AccessTime,
	Memory,
	HealthAndSafety,
	DeviceThermostat,
	FlashOn,
	Route,
	WarningAmber,
} from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import MainGauge from "./MainGauge";
import MiniGauge from "./MiniGauge";
import VehicleInfoCard from "./Card";
import GoogleTrailMap from "./GoogleMapTrail";
import { useVehicleData } from "../../../DataContext";

dayjs.extend(utc);
dayjs.extend(relativeTime);

const Dashboard = ({ onAlertsClick }) => {
	const { deviceId, chassisNum, canSnapshot, alerts, selectedVehicle } =
		useVehicleData();
	const devStatus = Array.isArray(canSnapshot?.header)
		? canSnapshot.header[1]
		: null;
	const vehStatus = Array.isArray(canSnapshot?.header)
		? canSnapshot.header[3]
		: null;
	const speed = canSnapshot?.can_data?.[0]?.rows?.[3] ?? null;
	const odometer = canSnapshot?.can_data?.[0]?.rows?.[0] ?? null;
	const voltage = canSnapshot?.can_data?.[2]?.rows?.[0] ?? null;
	const current = canSnapshot?.can_data?.[2]?.rows?.[1] ?? null;
	const soc = canSnapshot?.can_data?.[8]?.rows?.[2] ?? null;
	const soh = canSnapshot?.can_data?.[8]?.rows?.[3] ?? null;
	const batTemp = canSnapshot?.can_data?.[3]?.rows?.[3] ?? null;
	const accumCharge = canSnapshot?.can_data?.[1]?.rows?.[0] ?? null;
	const cycleCount = canSnapshot?.can_data?.[8]?.rows?.[4] ?? null;
	const dte = canSnapshot?.can_data?.[0]?.rows?.[2] ?? null;
	const [timestamp, setTimestamp] = useState(canSnapshot?.timestamp);
	const alertsCount = Array.isArray(alerts)
		? alerts.filter((a) => a.active).length
		: 0;
	const [lastSeen, setLastSeen] = useState("...");

	function PressTooltip({ title, holdDelay = 400, children, ...props }) {
		const isTouch = useMediaQuery("(hover: none)");
		const [open, setOpen] = useState(false);
		const holdTimer = useRef(null);

		const openLater = () => {
			clearTimeout(holdTimer.current);
			holdTimer.current = setTimeout(() => setOpen(true), holdDelay);
		};
		const clearAll = () => {
			clearTimeout(holdTimer.current);
			setOpen(false);
		};

		return (
			<Tooltip
				title={title}
				open={isTouch ? open : undefined}
				disableHoverListener={isTouch}
				onClose={() => setOpen(false)}
				{...props}
			>
				<Box
					component="span"
					sx={{ display: "inline-flex" }}
					// Touch: long-press to open, tap outside or short tap does nothing
					onTouchStart={isTouch ? openLater : undefined}
					onTouchEnd={isTouch ? clearAll : undefined}
					onTouchCancel={isTouch ? clearAll : undefined}
					// Optional: second tap toggles open if already shown
					onClick={
						isTouch
							? (e) => {
									e.preventDefault();
									setOpen((v) => !v);
							  }
							: undefined
					}
					// Desktop hover still works
					onMouseLeave={!isTouch ? () => setOpen(false) : undefined}
				>
					{children}
				</Box>
			</Tooltip>
		);
	}

	const handleAlertsClick = useCallback(
		(e) => {
			e.stopPropagation();
			if (typeof onAlertsClick === "function") onAlertsClick();
			else window.dispatchEvent(new CustomEvent("tm:open-alerts")); // fallback hook for parent
		},
		[onAlertsClick]
	);

	const formatDiff = useCallback((time) => {
		const now = dayjs();
		const diffSec = now.diff(time, "second");
		if (diffSec < 60) return `${diffSec}s ago`;
		const diffMin = now.diff(time, "minute");
		if (diffMin < 60) return `${diffMin}m ${diffSec % 60}s ago`;
		const diffHr = now.diff(time, "hour");
		if (diffHr < 24) return `${diffHr}h ${diffMin % 60}m ago`;
		const diffDay = now.diff(time, "day");
		if (diffDay < 7) return `${diffDay}d ${diffHr % 24}h ago`;
		const diffWeek = Math.floor(diffDay / 7);
		return `${diffWeek}w ${diffDay % 7}d ago`;
	}, []);

	function useRingBuffer(capacity, initial = []) {
		const [items, setItems] = useState(() => initial.slice(-capacity));
		const push = useCallback(
			(item) =>
				setItems((prev) => {
					if (!item) return prev;
					const last = prev[prev.length - 1];
					if (last && last.lat === item.lat && last.lng === item.lng)
						return prev;
					if (prev.length >= capacity) {
						const next = prev.slice(1);
						next.push(item);
						return next;
					}
					return [...prev, item];
				}),
			[capacity]
		);
		const clear = useCallback(() => setItems([]), []);
		return { items, push, clear };
	}

	// 2) Snapshot → {lat,lng}
	const extractPoint = (snap) => {
		const latLon = snap?.header?.[0]?.display_value;
		if (typeof latLon === "string") {
			const [latStr, lngStr] = latLon.split(",").map((s) => s.trim());
			const lat = parseFloat(latStr);
			const lng = parseFloat(lngStr);
			if (Number.isFinite(lat) && Number.isFinite(lng))
				return { lat, lng };
		}

		return null;
	};

	const { items: trail, push, clear } = useRingBuffer(90);

	useEffect(() => {
		setTimestamp(canSnapshot?.timestamp);
	}, [canSnapshot?.timestamp]);

	useEffect(() => {
		if (!timestamp) return;
		const id = setInterval(() => setLastSeen(formatDiff(timestamp)), 1000);
		return () => clearInterval(id);
	}, [timestamp, formatDiff]);

	useEffect(() => {
		clear();
	}, [selectedVehicle?.id, clear]);

	useEffect(() => {
		const pt = extractPoint(canSnapshot);
		if (pt) push(pt);
	}, [canSnapshot, push]);

	return (
		<Grid
			position="relative"
			zIndex={0}
			sx={{ height: { lg: "90vh", xs: "unset" } }} // was 90vh
		>
			<Grid>
				<Grid>
					{/* 2) Overlay map fills all available space on lg */}
					<Grid
						sx={{
							position: { lg: "absolute" },
							top: { lg: 0 },
							bottom: { lg: 0 },
							right: { lg: 0 },
							left: { lg: 0 },
							display: { lg: "block", xs: "none" },
							zIndex: 2,
						}}
					>
						<GoogleTrailMap points={trail} height="100%" />{" "}
						{/* pass 100% height */}
					</Grid>
				</Grid>

				<Grid
					sx={{
						position: "relative",
						zIndex: 300,
						width: { lg: "max-content" },
					}}
					spacing={2}
					container
				>
					<Grid
						item
						sx={{
							bgcolor: {
								lg: "rgba(255,255,255,0.6)",
								xs: "transparent",
							},
							"&:hover": {
								bgcolor: {
									lg: "rgba(255,255,255,0.8)",
									xs: "transparent",
								},
							},
							maxWidth: { lg: 380, xs: "unset" }, // was 500
							width: "100%",
							transform: { lg: "scale(0.9)" }, // shrink panel
							transformOrigin: { lg: "top left" },
						}}
						p={1}
						borderRadius={5}
					>
						<Box
							display="flex"
							alignItems="center"
							justifyContent="space-between"
							mb={1}
							flexWrap="wrap"
						>
							<Box
								display="flex"
								flexDirection="row"
								alignItems="center"
								gap={1}
								sx={{ color: devStatus?.color }}
							>
								{devStatus?.display_value === "ONLINE" ? (
									<SignalCellular4Bar sx={{ fontSize: 12 }} />
								) : (
									<SignalCellularOff sx={{ fontSize: 12 }} />
								)}
								<Typography variant="caption">
									{devStatus?.display_value}
								</Typography>
							</Box>

							<Box textAlign="center">
								<Typography
									sx={{
										wordBreak: "break-word",
										fontSize: 8,
									}}
								>
									{chassisNum}
								</Typography>
							</Box>

							<Box
								display="flex"
								alignItems="center"
								gap={1}
								sx={{ color: vehStatus?.color }}
							>
								{vehStatus?.display_value === "IDLE" ? (
									<HourglassFull sx={{ fontSize: 12 }} />
								) : vehStatus?.display_value === "MOVING" ? (
									<Moving sx={{ fontSize: 12 }} />
								) : vehStatus?.display_value === "CHARGING" ? (
									<EvStation sx={{ fontSize: 12 }} />
								) : (
									<ElectricRickshaw sx={{ fontSize: 12 }} />
								)}
								<Typography variant="caption">
									{vehStatus?.display_value}
								</Typography>
							</Box>
						</Box>

						<Divider sx={{ bgcolor: "#efebebff", mb: 1 }} />

						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								mb: 1,
							}}
						>
							{/* left: last update */}
							<Box display="flex" alignItems="center" gap={0.5}>
								<AccessTime sx={{ fontSize: 12 }} />
								<Typography sx={{ fontSize: 12 }}>
									{lastSeen}
								</Typography>
							</Box>
							<Box
								onClick={handleAlertsClick}
								role="button"
								tabIndex={0}
								onKeyDown={(e) =>
									(e.key === "Enter" || e.key === " ") &&
									handleAlertsClick(e)
								}
								sx={{
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
								}}
							>
								{/* right: alerts */}
								{alertsCount > 0 ? (
									<Badge
										badgeContent={alertsCount}
										color="error"
										sx={{
											"& .MuiBadge-badge": {
												fontSize: 9,
												height: 14,
												minWidth: 14,
											},
										}}
									>
										<WarningAmber
											sx={{
												fontSize: 14,
												color: "#c0392b",
											}}
										/>
									</Badge>
								) : (
									<Box
										display="flex"
										alignItems="center"
										gap={0.5}
										sx={{ color: "#27ae60" }}
									>
										<WarningAmber sx={{ fontSize: 14 }} />
										<Typography variant="caption">
											OK
										</Typography>
									</Box>
								)}
							</Box>
						</Box>
						<Grid
							container
							spacing={3}
							alignItems="center"
							justifyContent="center"
						>
							<Grid xs={12}>
								<Box
									display="flex"
									flexDirection="column"
									alignItems="center"
									gap={1.5}
								>
									{/* 5) Scale the main gauge on lg without changing the component API */}
									<Box
										sx={{
											transform: { lg: "scale(0.9)" },
											transformOrigin: "top center",
										}}
									>
										<MainGauge
											value={speed?.raw_value}
											max={speed?.range?.[1] || 60}
											color={speed?.color}
											odometer={odometer?.raw_value}
										/>
									</Box>
									{/* mini gauges row */}
									<Box sx={{ width: "100%", mt: 1 }}>
										<Grid
											container
											spacing={1}
											justifyContent="center"
										>
											<Grid xs={6} sm={3}>
												<MiniGauge
													value={soc?.raw_value}
													max={soc?.range?.[1] ?? 100}
													label={
														<PressTooltip
															title={`SoC: ${
																soc?.display_value ||
																"-"
															}`}
														>
															<BatteryChargingFull fontSize="small" />
														</PressTooltip>
													}
													unit="%"
													color={soc?.color}
												/>
											</Grid>
											<Grid xs={6} sm={3}>
												<MiniGauge
													value={soh?.raw_value}
													max={soh?.range?.[1] ?? 100}
													label={
														<PressTooltip
															title={`SoH: ${
																soh?.display_value ||
																"-"
															}`}
														>
															<HealthAndSafety fontSize="small" />
														</PressTooltip>
													}
													unit="%"
													color={soh?.color}
												/>
											</Grid>
											<Grid xs={6} sm={3}>
												<MiniGauge
													label={
														<PressTooltip
															title={`Battery Temperature: ${
																batTemp?.display_value ||
																"-"
															}`}
														>
															<DeviceThermostat fontSize="small" />
														</PressTooltip>
													}
													value={batTemp?.raw_value}
													max={
														batTemp?.range?.[1] ||
														100
													}
													unit={batTemp?.unit}
													color={batTemp?.color}
												/>
											</Grid>
											<Grid xs={6} sm={3}>
												<MiniGauge
													label={
														<PressTooltip
															title={`Accumulated Charge: ${
																accumCharge?.display_value ||
																"-"
															}`}
														>
															<Bolt fontSize="small" />
														</PressTooltip>
													}
													value={
														accumCharge?.raw_value
													}
													max={
														accumCharge
															?.range?.[1] || 230
													}
													unit={accumCharge?.unit}
													color={accumCharge?.color}
												/>
											</Grid>
										</Grid>
									</Box>
								</Box>
							</Grid>

							{/* Left column: Volt/Current, KWH, small stats */}
							<Grid xs={12}>
								<Box
									display="flex"
									flexDirection={{ xs: "row" }}
									gap={3}
									alignItems="center"
									flexWrap="wrap"
									justifyContent="center"
								>
									<PressTooltip
										title={`Voltage`}
										placement="top"
										arrow
									>
										<Box
											display="flex"
											alignItems="center"
											flexDirection="row"
											sx={{ color: voltage?.color }}
										>
											<BatteryChargingFull fontSize="small" />
											<Typography variant="subtitle2">
												{voltage?.display_value}
											</Typography>
										</Box>
									</PressTooltip>

									<PressTooltip
										title={`Current`}
										placement="top"
										arrow
									>
										<Box
											display="flex"
											alignItems="center"
											flexDirection="row"
											sx={{ color: current?.color }}
										>
											<FlashOn fontSize="small" />
											<Typography variant="subtitle2">
												{current?.display_value}
											</Typography>
										</Box>
									</PressTooltip>

									<PressTooltip
										title={`Cycle Count`}
										placement="top"
										arrow
									>
										<Box
											display="flex"
											alignItems="center"
											flexDirection="row"
										>
											<Loop fontSize="small" />
											<Typography variant="subtitle2">
												{cycleCount?.display_value}
											</Typography>
										</Box>
									</PressTooltip>

									<PressTooltip
										title={`Range`}
										placement="top"
										arrow
									>
										<Box
											display="flex"
											alignItems="center"
											flexDirection="row"
										>
											<Route fontSize="small" />
											<Typography variant="subtitle2">
												{dte?.display_value}
											</Typography>
										</Box>
									</PressTooltip>
								</Box>
							</Grid>
						</Grid>

						<Divider sx={{ bgcolor: "#222", mt: 2, mb: 1 }} />

						<Box display="flex" alignItems="center" gap={0.5}>
							<Memory sx={{ fontSize: 10 }} />
							<Typography sx={{ fontSize: 10 }}>
								{deviceId}
							</Typography>
						</Box>
					</Grid>

					<Grid
						item
						sx={{
							display: { xs: "block", lg: "none" },
							width: "100%",
						}}
					>
						<GoogleTrailMap points={trail} />
					</Grid>
				</Grid>
			</Grid>
			<Grid
				sx={{
					zIndex: 3,
					position: { lg: "absolute", xs: "relative" },
					bottom: 0,
					right: 0,
					marginBlock: "20px",
				}}
			>
				<VehicleInfoCard
					bgColor="rgba(255,255,255,0.6)"
					width={{ lg: 280, xs: "350px" }} // was 350px
					selectedVehicle={selectedVehicle}
				/>
			</Grid>
		</Grid>
	);
};

export default Dashboard;
